import React, { useCallback, useEffect, useState } from "react";
import { maintenanceApi, ProcOrder, ProcOutstandingItem } from "../../../api/maintenance";
import { ORDER_STATUS, fmtDate, errMsg } from "./procUtils";
import OrderDrawer from "./OrderDrawer";
import { AlertTriangle, ChevronLeft, ChevronRight } from "lucide-react";

interface Props {
  refreshKey: number;
  onChanged: (msg?: string) => void;
  onError: (msg: string) => void;
}

const PAGE_SIZE = 50;
const AGED_DAYS = 90;

/**
 * Orders — a dispatch is a document, not a "send email" action. Build it as a
 * draft, issue it (that's the state change), then receive against it in as many
 * shipments as it actually arrives in.
 */
const OrdersTab: React.FC<Props> = ({ refreshKey, onChanged, onError }) => {
  const [orders, setOrders] = useState<ProcOrder[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(false);
  const [openId, setOpenId] = useState<number | null>(null);
  const [aged, setAged] = useState<ProcOutstandingItem[]>([]);

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const res = await maintenanceApi.procGetOrders({
        status: statusFilter || undefined,
        page,
        page_size: PAGE_SIZE,
      });
      setOrders(res.data.items);
      setTotal(res.data.total);
    } catch (e: any) {
      onError(errMsg(e, "Failed to load orders"));
    } finally {
      setLoading(false);
    }
  }, [statusFilter, page, onError]);

  useEffect(() => {
    fetchOrders();
    maintenanceApi
      .procGetOutstanding(AGED_DAYS)
      .then((r) => setAged(r.data))
      .catch(() => setAged([]));
  }, [fetchOrders, refreshKey]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <>
      {aged.length > 0 && (
        <div className="proc-banner warn">
          <AlertTriangle size={16} />
          <span>
            <b>{aged.length}</b> unit{aged.length === 1 ? " has" : "s have"} been outstanding for
            over {AGED_DAYS} days —{" "}
            {Array.from(new Set(aged.map((a) => a.manufacturer_name).filter(Boolean))).join(", ")}.
            Chase the manufacturer, or short-close the order to write them off.
          </span>
        </div>
      )}

      <div className="maint-toolbar">
        <label style={{ fontSize: 13, fontWeight: 500, color: "#555" }}>
          Status{" "}
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(1);
            }}
            style={{ marginLeft: 6, padding: "6px 10px", borderRadius: 6, border: "1px solid #e0e0e0" }}
          >
            <option value="">All</option>
            <option value="draft">Draft</option>
            <option value="issued">Issued</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </label>
        <div className="proc-mini">
          {total} order{total === 1 ? "" : "s"}
        </div>
      </div>

      {loading ? (
        <p className="maint-loading">Loading…</p>
      ) : (
        <table className="maint-table">
          <thead>
            <tr>
              <th>Order</th>
              <th>Status</th>
              <th>Manufacturer</th>
              <th>Units</th>
              <th title="received OK / damaged / still outstanding">ok / dmg / out</th>
              <th>Dates</th>
              <th>Delivery</th>
            </tr>
          </thead>
          <tbody>
            {orders.map((o) => {
              const st = ORDER_STATUS[o.effective_status] || { label: o.status, cls: "grey" };
              return (
                <tr key={o.id} className="clickable" onClick={() => setOpenId(o.id)}>
                  <td className="proc-mono">{o.order_no}</td>
                  <td>
                    <span className={`proc-pill ${st.cls}`}>{st.label}</span>
                  </td>
                  <td>{o.manufacturer_name}</td>
                  <td>{o.total_items}</td>
                  <td>
                    {o.status === "draft" ? (
                      <span style={{ color: "#bbb" }}>—</span>
                    ) : (
                      <>
                        <b style={{ color: "#2e7d32" }}>{o.ok_count}</b>
                        {" / "}
                        <b style={{ color: "#c62828" }}>{o.damaged_count}</b>
                        {" / "}
                        <b style={{ color: "#e65100" }}>{o.outstanding_count}</b>
                      </>
                    )}
                  </td>
                  <td className="proc-mini">
                    {fmtDate(o.created_at)}
                    {o.issued_on && (
                      <>
                        <br />
                        issued {fmtDate(o.issued_on)}
                      </>
                    )}
                  </td>
                  <td>
                    {o.delivery_count === 0 ? (
                      <span className="proc-mini">not delivered</span>
                    ) : o.last_delivery_status === "failed" ? (
                      <span className="proc-pill red">email failed</span>
                    ) : (
                      <span className="proc-mini">
                        {o.delivery_count} attempt{o.delivery_count === 1 ? "" : "s"} · ok
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
            {orders.length === 0 && (
              <tr>
                <td colSpan={7} className="maint-empty">
                  No orders yet — select devices in the pool and choose <b>Add to order</b>.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}

      {totalPages > 1 && (
        <div className="maint-pagination">
          <span>
            Page {page} of {totalPages}
          </span>
          <div className="maint-pagination-controls">
            <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
              <ChevronLeft size={16} />
            </button>
            <button disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      )}

      {openId !== null && (
        <OrderDrawer
          orderId={openId}
          onClose={() => setOpenId(null)}
          onChanged={(msg) => onChanged(msg)}
        />
      )}
    </>
  );
};

export default OrdersTab;
